import {
    DndContext,
    type DragEndEvent,
    PointerSensor,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { type ReactElement, useMemo } from 'react';

import { type ActivityAssigneeOption } from '@apps/web/components/home/activity_assignment';
import { ConversationBoardCard } from '@apps/web/components/shared/messages/conversation_board_card';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { cn } from '@apps/web/lib/utils';
import { readConversationOwnership } from '@apps/web/swr_data_fetching/conversation_ownership';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

// Sentinel droppable/column id for conversations with no assignee. Real
// assignee ids are user UUIDs, so this never collides.
const UNASSIGNED_COLUMN_ID = 'unassigned';

type ConversationBoardProps = {
    readonly organizationId: string;
    readonly currentUserId: string | null;
    readonly timezone: string;
    readonly conversations: Array<OrganizationConversationMetadata>;
    readonly search: string;
    readonly assignees: ReadonlyArray<ActivityAssigneeOption>;
    readonly selectedConversationId: string | null;
    readonly hasMore: boolean;
    readonly isLoadingMore: boolean;
    readonly onLoadMore: () => void;
    readonly onSelectConversation: (conversationId: string) => void;
    readonly onReassign: (
        conversationId: string,
        assignedToUserId: string | null,
    ) => void;
};

type BoardColumn = {
    readonly id: string;
    readonly title: string;
    readonly assigneeId: string | null;
    readonly conversations: Array<OrganizationConversationMetadata>;
};

function BoardColumnDropZone({
    column,
    organizationId,
    currentUserId,
    timezone,
    selectedConversationId,
    onSelectConversation,
}: {
    readonly column: BoardColumn;
    readonly organizationId: string;
    readonly currentUserId: string | null;
    readonly timezone: string;
    readonly selectedConversationId: string | null;
    readonly onSelectConversation: (conversationId: string) => void;
}): ReactElement {
    const { isOver, setNodeRef } = useDroppable({ id: column.id });

    return (
        <div className="flex w-72 shrink-0 flex-col">
            <div className="flex items-center justify-between gap-2 px-1 pb-2">
                <span className="text-muted-foreground truncate text-xs font-semibold tracking-wide uppercase">
                    {column.title}
                </span>
                <Badge variant="secondary" className="shrink-0 tabular-nums">
                    {column.conversations.length}
                </Badge>
            </div>
            <div
                ref={setNodeRef}
                className={cn(
                    'bg-muted/40 flex min-h-24 flex-1 flex-col gap-2 rounded-xl p-2 transition-colors',
                    isOver && 'bg-primary/5 ring-primary/25 ring-2',
                )}
            >
                {column.conversations.length === 0 ? (
                    <p className="text-muted-foreground/70 dark:text-muted-foreground px-1 py-6 text-center text-xs">
                        No conversations
                    </p>
                ) : (
                    column.conversations.map((conversation) => (
                        <ConversationBoardCard
                            key={conversation.id}
                            conversation={conversation}
                            organizationId={organizationId}
                            currentUserId={currentUserId}
                            timezone={timezone}
                            isSelected={
                                conversation.id === selectedConversationId
                            }
                            onSelect={() =>
                                onSelectConversation(conversation.id)
                            }
                        />
                    ))
                )}
            </div>
        </div>
    );
}

export function ConversationBoard({
    organizationId,
    currentUserId,
    timezone,
    conversations,
    search,
    assignees,
    selectedConversationId,
    hasMore,
    isLoadingMore,
    onLoadMore,
    onSelectConversation,
    onReassign,
}: ConversationBoardProps): ReactElement {
    // Require a small drag distance before a card starts dragging so that a
    // click to open a conversation is not interpreted as a drag.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    );

    const columns = useMemo<Array<BoardColumn>>(() => {
        const byAssignee = new Map<
            string,
            Array<OrganizationConversationMetadata>
        >();
        const labelByAssignee = new Map<string, string>();
        for (const conversation of conversations) {
            const ownership = readConversationOwnership(conversation);
            const key = ownership.assigned_to_user_id ?? UNASSIGNED_COLUMN_ID;
            if (
                ownership.assigned_to_user_id &&
                ownership.assigned_to_user_name
            ) {
                labelByAssignee.set(
                    ownership.assigned_to_user_id,
                    ownership.assigned_to_user_name,
                );
            }
            const existing = byAssignee.get(key);
            if (existing) {
                existing.push(conversation);
            } else {
                byAssignee.set(key, [conversation]);
            }
        }

        // Union of assignees that own a card and assignees offered by the
        // picker, so an empty column still appears as a valid drop target.
        const assigneeIds = new Set<string>();
        for (const key of byAssignee.keys()) {
            if (key !== UNASSIGNED_COLUMN_ID) {
                assigneeIds.add(key);
            }
        }
        for (const assignee of assignees) {
            assigneeIds.add(assignee.id);
            labelByAssignee.set(
                assignee.id,
                labelByAssignee.get(assignee.id) ?? assignee.label,
            );
        }

        const sortConversations = (
            list: Array<OrganizationConversationMetadata>,
        ) => {
            if (search.trim()) {
                return list;
            }
            return [...list].sort(
                (left, right) =>
                    new Date(right.last_active_at).getTime() -
                    new Date(left.last_active_at).getTime(),
            );
        };

        // Unassigned column always leads.
        const orderedColumns: Array<BoardColumn> = [
            {
                id: UNASSIGNED_COLUMN_ID,
                title: 'Unassigned',
                assigneeId: null,
                conversations: sortConversations(
                    byAssignee.get(UNASSIGNED_COLUMN_ID) ?? [],
                ),
            },
        ];

        const assigneeColumns = Array.from(assigneeIds)
            .map((assigneeId) => ({
                id: assigneeId,
                title: labelByAssignee.get(assigneeId) ?? 'Assigned',
                assigneeId,
                conversations: sortConversations(
                    byAssignee.get(assigneeId) ?? [],
                ),
            }))
            .sort((left, right) => left.title.localeCompare(right.title));

        return [...orderedColumns, ...assigneeColumns];
    }, [conversations, assignees, search]);

    const handleDragEnd = (event: DragEndEvent) => {
        const conversationId = String(event.active.id);
        const targetColumnId = event.over ? String(event.over.id) : null;
        if (!targetColumnId) {
            return;
        }
        const conversation = conversations.find(
            (candidate) => candidate.id === conversationId,
        );
        if (!conversation) {
            return;
        }
        const currentAssigneeId =
            readConversationOwnership(conversation).assigned_to_user_id;
        const targetAssigneeId =
            targetColumnId === UNASSIGNED_COLUMN_ID ? null : targetColumnId;
        if (currentAssigneeId === targetAssigneeId) {
            return;
        }
        onReassign(conversationId, targetAssigneeId);
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto p-3">
                    {columns.map((column) => (
                        <BoardColumnDropZone
                            key={column.id}
                            column={column}
                            organizationId={organizationId}
                            currentUserId={currentUserId}
                            timezone={timezone}
                            selectedConversationId={selectedConversationId}
                            onSelectConversation={onSelectConversation}
                        />
                    ))}
                </div>
            </DndContext>
            {hasMore ? (
                <div className="flex justify-center border-t px-3 py-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        disabled={isLoadingMore}
                        onClick={onLoadMore}
                    >
                        {isLoadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
